import React from "react";

/**
 * InvoiceEmailPreview — a scaled-down, static preview of what the invoice-email
 * template looks like in the customer's inbox. Purely visual, no data bindings.
 * Matches the three backend HTML templates in email_service.py.
 */
const SAMPLE = {
    number: "INV-2048",
    customer: "Acme Industries Ltd.",
    due: "Feb 28, 2026",
    subtotal: "KYD 2,400.00",
    total: "KYD 2,400.00",
    lines: [
        ["Website design — homepage", "KYD 1,200.00"],
        ["Brand guidelines", "KYD 800.00"],
        ["Hosting (3 months)", "KYD 400.00"],
    ],
};

export function InvoiceEmailPreview({ template = "classic", orgName = "Your business", compact = false }) {
    if (compact) {
        // Scaled-down thumbnail: 720x540 source scaled to fit a responsive card.
        return (
            <div className="relative w-full overflow-hidden bg-sand-50/40" style={{ aspectRatio: "720 / 540" }} data-testid={`template-preview-${template}`}>
                <div
                    className="absolute top-0 left-0 origin-top-left pointer-events-none"
                    style={{ width: 720, height: 540, transform: "scale(var(--tpl-scale, 0.4))" }}
                >
                    {template === "modern" ? <ModernPreview orgName={orgName} /> :
                     template === "minimal" ? <MinimalPreview orgName={orgName} /> :
                     <ClassicPreview orgName={orgName} />}
                </div>
                {/* ResizeObserver-free scaling: use a CSS container query fallback via inline script element */}
                <ScaleToFit />
            </div>
        );
    }
    return (
        <div className="w-full overflow-auto bg-sand-50/40" data-testid={`template-preview-${template}`}>
            <div style={{ width: 720 }} className="mx-auto">
                {template === "modern" ? <ModernPreview orgName={orgName} /> :
                 template === "minimal" ? <MinimalPreview orgName={orgName} /> :
                 <ClassicPreview orgName={orgName} />}
            </div>
        </div>
    );
}

/** Adjusts the --tpl-scale CSS variable on the parent to keep the 720×540 preview
 *  scaled to the available container width, with no content clipping. */
function ScaleToFit() {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const container = ref.current?.parentElement;
        if (!container) return;
        const inner = container.querySelector("[data-scale-src]");
        const update = () => {
            const w = container.getBoundingClientRect().width;
            if (w > 0) container.style.setProperty("--tpl-scale", String(w / 720));
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(container);
        return () => ro.disconnect();
    }, []);
    return <span ref={ref} aria-hidden data-scale-src style={{ display: "none" }} />;
}

function Shell({ children }) {
    return (
        <div style={{ width: 720, fontFamily: "Arial, sans-serif", color: "#0B2B40" }} className="bg-white">
            {children}
        </div>
    );
}

function ClassicPreview({ orgName }) {
    return (
        <Shell>
            <div style={{ padding: 24 }}>
                <div style={{ fontSize: 12, color: "#5b7085", textTransform: "uppercase", letterSpacing: 1.2 }}>{orgName}</div>
                <div style={{ height: 1, background: "#E3ECF5", margin: "14px 0" }} />
                <div style={{ fontSize: 22, fontWeight: 700 }}>Invoice {SAMPLE.number}</div>
                <div style={{ fontSize: 13, color: "#5b7085", marginTop: 4 }}>
                    To {SAMPLE.customer} · Due {SAMPLE.due}
                </div>
                <div style={{ background: "#EAF8FA", border: "1px solid #BFE8EE", borderRadius: 14, padding: 18, marginTop: 18 }}>
                    <div style={{ fontSize: 11, color: "#00788a", textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 600 }}>Amount due</div>
                    <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4, color: "#0B2B40" }}>{SAMPLE.total}</div>
                </div>
                <table style={{ width: "100%", marginTop: 18, fontSize: 13, borderCollapse: "collapse" }}>
                    <tbody>
                        {SAMPLE.lines.map(([d, a], i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #EFF3F8" }}>
                                <td style={{ padding: "8px 0" }}>{d}</td>
                                <td style={{ padding: "8px 0", textAlign: "right", fontFamily: "Menlo, monospace" }}>{a}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ textAlign: "right", marginTop: 12, fontSize: 14, fontWeight: 600 }}>Total {SAMPLE.total}</div>
                <div style={{ marginTop: 18, padding: 14, background: "#F5F8FB", borderRadius: 12, fontSize: 12, color: "#5b7085" }}>
                    Payment is due within 30 days. Thank you for your business.
                </div>
            </div>
        </Shell>
    );
}

function ModernPreview({ orgName }) {
    return (
        <Shell>
            <div style={{ background: "linear-gradient(135deg, #0B2B40 0%, #134566 100%)", color: "white", padding: 28 }}>
                <div style={{ fontSize: 12, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1.4 }}>{orgName}</div>
                <div style={{ fontSize: 26, fontWeight: 800, marginTop: 8 }}>Invoice {SAMPLE.number}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 20 }}>
                    <div>
                        <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1.2 }}>Billed to</div>
                        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{SAMPLE.customer}</div>
                        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 10, textTransform: "uppercase", letterSpacing: 1.2 }}>Due</div>
                        <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>{SAMPLE.due}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1.2 }}>Total</div>
                        <div style={{ fontSize: 34, fontWeight: 800, color: "#FF7F6A", marginTop: 4 }}>{SAMPLE.total}</div>
                    </div>
                </div>
            </div>
            <div style={{ padding: 24 }}>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ textAlign: "left", color: "#5b7085", textTransform: "uppercase", fontSize: 10, letterSpacing: 1.2 }}>
                            <th style={{ padding: "6px 0" }}>Item</th>
                            <th style={{ padding: "6px 0", textAlign: "right" }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {SAMPLE.lines.map(([d, a], i) => (
                            <tr key={i} style={{ borderTop: "1px solid #EFF3F8" }}>
                                <td style={{ padding: "9px 0" }}>{d}</td>
                                <td style={{ padding: "9px 0", textAlign: "right", fontFamily: "Menlo, monospace" }}>{a}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ marginTop: 18, textAlign: "right" }}>
                    <a style={{ display: "inline-block", background: "#FF7F6A", color: "white", padding: "10px 22px", borderRadius: 999, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                        Pay invoice
                    </a>
                </div>
            </div>
        </Shell>
    );
}

function MinimalPreview({ orgName }) {
    return (
        <Shell>
            <div style={{ padding: 30, fontFamily: "Georgia, serif", color: "#1a1a1a" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #1a1a1a", paddingBottom: 14 }}>
                    <div>
                        <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 2.5, color: "#5b7085" }}>Invoice</div>
                        <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{SAMPLE.number}</div>
                    </div>
                    <div style={{ fontSize: 13, textAlign: "right", color: "#5b7085" }}>
                        <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{orgName}</div>
                        <div style={{ marginTop: 2 }}>Due {SAMPLE.due}</div>
                    </div>
                </div>
                <div style={{ marginTop: 18 }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5, color: "#5b7085" }}>Billed to</div>
                    <div style={{ fontSize: 14, marginTop: 3 }}>{SAMPLE.customer}</div>
                </div>
                <table style={{ width: "100%", marginTop: 18, fontSize: 13, borderCollapse: "collapse" }}>
                    <tbody>
                        {SAMPLE.lines.map(([d, a], i) => (
                            <tr key={i}>
                                <td style={{ padding: "8px 0", borderBottom: "1px dotted #C9D4E2" }}>{d}</td>
                                <td style={{ padding: "8px 0", textAlign: "right", borderBottom: "1px dotted #C9D4E2", fontFamily: "Menlo, monospace" }}>{a}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", fontSize: 15 }}>
                    <div style={{ textTransform: "uppercase", letterSpacing: 1.5, color: "#5b7085" }}>Total</div>
                    <div style={{ fontWeight: 700, fontFamily: "Menlo, monospace" }}>{SAMPLE.total}</div>
                </div>
            </div>
        </Shell>
    );
}

export default InvoiceEmailPreview;
